/**
 * Data transformation utilities for converting Shopify/internal data to InstantJS format
 */

/**
 * Transform product data to InstantJS InteractionProduct format
 * @param {Object} product - Product data from Shopify or internal format
 * @returns {Object} InstantJS InteractionProduct
 */
export const buildInstantProductData = (product) => {
  if (!product) return null

  return {
    product_id: product.id || product.product_id || product.globalProduct?.['en-AU']?.meta_id || '',
    product_sku: product.selectedVariant?.SKU || product.sku || product.product_sku || product.globalProduct?.['en-AU']?.sku || '',
    product_name: product.title || product.name || product.product_name,
    product_description: product.description || product.product_description,
    product_brand: product.vendor || product.brand || product.product_brand || 'July',
    product_type: product.product_type || product.type || product.Category || product.Range,
    variant_id: product.selectedVariant?.shopifyId || product.selectedVariant?.metaShopifyInfo?.shopifyId || product.variant_id || product.id || '',
    variant_sku: product.selectedVariant?.SKU || product.sku || product.variant_sku || product.globalProduct?.['en-AU']?.sku || '',
    variant_title: product.selectedVariant?.ColourName || product.selectedVariant?.title || product.variant_title || product.variant || product.globalProduct?.['en-AU']?.title || '',
    price: product.selectedVariant?.price?.amount
      ? Math.round(parseFloat(product.selectedVariant.price.amount) * 100)
      : product.selectedVariant?.price
        ? Math.round(parseFloat(product.selectedVariant.price) * 100)
        : product.price
          ? Math.round(parseFloat(product.price) * 100)
          : product.globalProduct?.['en-AU']?.price
            ? Math.round(parseFloat(product.globalProduct['en-AU'].price) * 100)
            : null,
    currency: product.currency || getCurrentCurrency(),
    categories:
      product.collections?.map((col) => ({
        id: col.id,
        name: col.title || col.name,
      })) || 
      product.product_categories?.map((cat, index) => ({
        id: `${cat}-${index}`,
        name: cat,
      })) ||
      product.categories ||
      (product.Category ? [{ id: product.Category, name: product.Category }] : []),
    affiliation: product.affiliation || '',
    options:
      product.options?.map((opt) => ({
        label: opt.name || opt.label,
        value: opt.selectedValue || opt.value,
        option_id: opt.id || opt.option_id,
        option_value: opt.selectedValue || opt.option_value || opt.value,
      })) || 
      (product.selectedVariant?.ColourName ? [{
        label: 'Color',
        value: product.selectedVariant.ColourName,
        option_id: 'color',
        option_value: product.selectedVariant.ColourName,
      }] : []),
    image: {
      url: product.selectedVariant?.ShopTileImage?.url || 
           product.featuredImage?.url || 
           product.image?.url || 
           product.imageUrl || 
           product.imageURL ||
           product.globalProduct?.['en-AU']?.image?.gatsbyImageData?.images?.fallback?.src ||
           '',
      alt: product.selectedVariant?.ShopTileImage?.alternativeText || product.featuredImage?.altText || product.image?.alt || product.title,
      width: product.selectedVariant?.ShopTileImage?.width || product.featuredImage?.width || product.image?.width,
      height: product.selectedVariant?.ShopTileImage?.height || product.featuredImage?.height || product.image?.height,
      aspect_ratio:
        product.featuredImage?.aspectRatio || product.image?.aspect_ratio,
    },
    handle: product.handle,
    online_store_url: buildProductUrl(product),
  }
}

/**
 * Transform cart data to InstantJS InteractionCart format
 * @param {Object} cart - Cart data from Shopify or internal format
 * @returns {Object} InstantJS InteractionCart
 */
export const buildInstantCartData = (cart) => {
  if (!cart) return null

  return {
    cart_url: getFullCartUrl(cart) || cart.checkoutUrl || cart.webUrl,
    cart_id: cart.id || cart.cart_id,
    currency: cart.currency || cart.subtotalPrice?.currencyCode || 'AUD',
    transaction_id: (cart.checkoutUrl || cart.webUrl)?.split('/').pop()?.split('?')[0] || cart.transaction_id,
    tax_total: cart.totalTax
      ? Math.round(parseFloat(cart.totalTax) * 100)
      : cart.tax_total,
    sub_total: cart.subtotalPrice?.amount
      ? Math.round(parseFloat(cart.subtotalPrice.amount) * 100)
      : cart.lineItemsSubtotalPrice?.amount
        ? Math.round(parseFloat(cart.lineItemsSubtotalPrice.amount) * 100)
        : cart.subtotal?.amount
          ? Math.round(parseFloat(cart.subtotal.amount) * 100)
          : cart.subtotal
            ? Math.round(parseFloat(cart.subtotal) * 100)
            : cart.sub_total || 0,
    shipping_total: cart.shippingCost
      ? Math.round(parseFloat(cart.shippingCost) * 100)
      : cart.shipping_total,
    grand_total: cart.totalPrice?.amount
      ? Math.round(parseFloat(cart.totalPrice.amount) * 100)
      : cart.total?.amount
        ? Math.round(parseFloat(cart.total.amount) * 100)
        : cart.total
          ? Math.round(parseFloat(cart.total) * 100)
          : cart.grand_total || 0,
    discounts:
      cart.discounts?.map((discount) => ({
        id: discount.code || discount.id,
        type: 'DISCOUNT_CODE',
        percentage: discount.percentage || 0,
        amount: discount.amount
          ? Math.round(parseFloat(discount.amount) * 100)
          : 0,
      })) || [],
    items: cart.lineItems?.edges?.map((edge) => buildInstantCartItemData(edge.node)) || 
           cart.items?.map((item) => buildInstantCartItemData(item)) || [],
  }
}

/**
 * Transform cart item to InstantJS InteractionCartItem format
 * @param {Object} item - Cart item data
 * @returns {Object} InstantJS InteractionCartItem
 */
export const buildInstantCartItemData = (item) => {
  if (!item) return null

  // Handle itemData format (has selectedVariant) - this is the main one
  if (item.selectedVariant) {
    return {
      item_id: item.id || '',
      product_id: item.selectedVariant.metaShopifyInfo?.shopifyId || '',
      product_sku: item.selectedVariant.SKU || '',
      product_name: item.title || '',
      variant_id: item.selectedVariant.metaShopifyInfo?.shopifyId || '',
      variant_sku: item.selectedVariant.SKU || '',
      variant_title: item.selectedVariant.ColourName || '',
      price: item.price ? Math.round(parseFloat(item.price) * 100) : 0,
      quantity: item.quantity || 1,
      currency: item.currency || 'AUD',
      image: {
        url: item.imageURL || ''
      },
      item_name: item.title || '',
      item_sku: item.selectedVariant.SKU || ''
    }
  }

  // Handle Shopify cart line item format (has variant.id structure)
  if (item.variant && item.variant.id) {
    return {
      item_id: item.id || '',
      product_id: item.variant.id || '',
      product_sku: item.variant.sku || '',
      product_name: item.title || '',
      variant_id: item.variant.id || '',
      variant_sku: item.variant.sku || '',
      variant_title: item.variant.title || '',
      price: item.variant.price?.amount ? Math.round(parseFloat(item.variant.price.amount) * 100) : 0,
      quantity: item.quantity || 1,
      currency: item.variant.price?.currencyCode || 'AUD',
      image: {
        url: item.variant.image?.src || ''
      },
      item_name: item.title || '',
      item_sku: item.variant.sku || ''
    }
  }

  // Handle internal cart format (has globalProduct only)
  if (item.globalProduct && !item.selectedVariant) {
    return {
      item_id: item.id || '',
      product_id: item.globalProduct['en-AU']?.meta_id || '',
      product_sku: item.globalProduct['en-AU']?.sku || '',
      product_name: item.title || '',
      variant_id: item.globalProduct['en-AU']?.meta_id || '',
      variant_sku: item.globalProduct['en-AU']?.sku || '',
      variant_title: item.globalProduct['en-AU']?.title || '',
      price: item.globalProduct['en-AU']?.price ? Math.round(parseFloat(item.globalProduct['en-AU'].price) * 100) : 0,
      quantity: item.quantity || 1,
      currency: item.currency || 'AUD',
      image: {
        url: item.globalProduct['en-AU']?.image?.gatsbyImageData?.images?.fallback?.src || ''
      },
      item_name: item.title || '',
      item_sku: item.globalProduct['en-AU']?.sku || ''
    }
  }

  // Fallback
  return {
    item_id: item.id || '',
    product_id: item.id || '',
    product_sku: item.sku || '',
    product_name: item.title || '',
    variant_id: item.id || '',
    variant_sku: item.sku || '',
    variant_title: item.variant || '',
    price: item.price ? Math.round(parseFloat(item.price) * 100) : 0,
    quantity: item.quantity || 1,
    currency: item.currency || 'AUD',
    image: {
      url: item.imageURL || ''
    },
    item_name: item.title || '',
    item_sku: item.sku || ''
  }
}

/**
 * Transform collection data for InstantJS tracking
 * @param {Array} products - Products in the collection
 * @returns {Object} Formatted collection data for InstantJS
 */
export const buildInstantCollectionData = (products = []) => {
  return {
    products: products
      .map((product) => buildInstantProductData(product))
      .filter(Boolean),
  }
}

/**
 * Helper function to safely parse price values
 * @param {string|number} price - Price value to parse
 * @returns {number|null} Price in cents or null
 */
export const parsePrice = (price) => {
  if (!price) return null
  const parsed = parseFloat(price)
  return isNaN(parsed) ? null : Math.round(parsed * 100)
}

/**
 * Helper function to get current currency based on URL or default
 * @returns {string} Currency code
 */
export const getCurrentCurrency = () => {
  if (typeof window === 'undefined') return 'AUD'
  
  const url = window.location.href
  if (url.includes('/us/')) return 'USD'
  if (url.includes('/uk/')) return 'GBP'
  if (url.includes('/ca/')) return 'CAD'
  if (url.includes('/nz/')) return 'NZD'
  return 'AUD' // Default to AUD
}

/**
 * Helper function to build full product URL
 * @param {Object} product - Product data
 * @returns {string} Full product URL
 */
export const buildProductUrl = (product) => {
  if (typeof window === 'undefined') return ''
  
  const baseUrl = window.location.origin
  const handle = product.handle || product.slug
  const variant = product.selectedVariant?.ColourName || product.variant
  
  if (!handle) return baseUrl
  
  let productUrl = `${baseUrl}/luggage/${handle}/`
  if (variant) {
    productUrl += `?${encodeURIComponent(variant)}`
  }
  
  return productUrl
}

/**
 * Helper function to get full unique cart URL
 * @param {Object} cart - Cart data
 * @returns {string} Full unique cart URL
 */
export const getFullCartUrl = (cart) => {
  if (typeof window === 'undefined') return ''
  
  try {
    const checkoutData = JSON.parse(localStorage.getItem('checkout_id_v2'))
    if (checkoutData && checkoutData[1]?.webUrl) {
      return checkoutData[1].webUrl.replace(
        'july-travel.myshopify.com',
        'checkout.july.com'
      )
    }
  } catch (e) {
    // Fallback if checkout_id_v2 is not available
  }
  
  // Fallback to basic cart URL
  return `${window.location.origin}/cart`
}